import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useGeminiLanguage } from 'canvas/hooks/useGeminiLanguage';
import { normalizeCanvasLocale } from 'canvas/utils/canvasI18n';
import extra2 from '../home/extra2.png';
import canvasRightClickImage from '../../canvas/assets/pin-from-canvas-rightclick.png';

const COPY = {
  en: {
    emptyChip: "You haven't collected anything yet",
    createChip: 'New board',
    createTitleLead: 'Create a new',
    createTitleAccent: 'AI insight board',
    createSubtitle:
      'Save anything to your board. AI will discover insights from it and help you with your goals.',
    hashtagLabel: 'Board name',
    hashtagPlaceholder: 'ex) japan',
    descriptionLabel: 'What is your goal?',
    descriptionPlaceholder: 'ex) I want to plan my trip to Japan',
    createCta: 'Create canvas',
    creating: 'Creating…',
    pinTitle: 'How to pin anything to your canvas',
    step1Title: 'Right-click on the target and select "Pin it to your Ai canvas"',
    step1Description: 'Pick exactly what you need on the page and save it instantly.',
    step2Title: 'Anything else (screenshot)',
    step2Description:
      'On a Mac, capture a screenshot with ⌘⇧4, then paste it onto the canvas (⌘V). The same paste works for text and links. Right-click empty space to type a note, or pin from in-app chat.',
  },
  ko: {
    emptyChip: '아직 수집한 항목이 없습니다',
    createChip: '새 보드',
    createTitleLead: '새 AI 메모리 보드',
    createTitleAccent: '만들기',
    createSubtitle:
      '보드 이름과 목표를 정하세요. 그다음 웹에서 원하는 것을 핀할 수 있습니다.',
    hashtagLabel: '캔버스 이름',
    hashtagPlaceholder: '예: japan',
    descriptionLabel: '보드 목표',
    descriptionPlaceholder: '이 보드는 무엇을 위한 건가요?',
    createCta: '캔버스 만들기',
    creating: '만드는 중…',
    pinTitle: '사용 방법',
    step1Title: '우클릭 후 "Pin it to your Ai canvas"로 선택 시작',
    step1Description: '원하는 영역을 선택해 바로 수집할 수 있습니다.',
    step2Title: '그 외 (스크린샷, 텍스트, 메모)',
    step2Description:
      'Mac에서는 ⌘⇧4로 스크린샷을 찍은 뒤 캔버스에 붙여넣으세요(⌘V). 텍스트와 링크도 같은 방법으로 붙여넣을 수 있고, 빈 곳을 우클릭하면 메모를 입력하거나 채팅에서 핀할 수 있습니다.',
  },
};

function getCopy(language) {
  const locale = normalizeCanvasLocale(language);
  return COPY[locale] || COPY.en;
}

const shellSx = {
  minHeight: '100%',
  height: '100%',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
  py: { xs: 6, md: 8 },
  px: { xs: 2, sm: 3 },
  background: 'linear-gradient(165deg, #f7f9ff 0%, #eef3ff 35%, #dce9ff 100%)',
};

const fieldSx = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid rgba(15, 23, 42, 0.1)',
  borderRadius: '14px',
  px: 1.75,
  py: 1.5,
  fontSize: 15,
  lineHeight: 1.45,
  fontFamily: 'inherit',
  color: '#0f172a',
  bgcolor: '#fff',
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: '#94a3b8' },
  '&:hover': { borderColor: 'rgba(15, 23, 42, 0.18)' },
  '&:focus': {
    borderColor: '#6366f1',
    boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.14)',
  },
  '&:disabled': {
    bgcolor: '#f8fafc',
    color: '#94a3b8',
  },
};

const CreateCanvasGuide = ({ onCreateCanvas, creating, error }) => {
  const { geminiLanguage } = useGeminiLanguage();
  const copy = getCopy(geminiLanguage);
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const boardName = value.trim();

  const handleSubmit = e => {
    e.preventDefault();
    if (creating || !onCreateCanvas) return;
    onCreateCanvas(value, description);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        boxSizing: 'border-box',
        p: { xs: 1.5, sm: 2.5, md: 3 },
        background:
          'radial-gradient(820px 420px at 0% 0%, rgba(99, 102, 241, 0.16), transparent 58%), radial-gradient(720px 420px at 100% 100%, rgba(14, 165, 233, 0.12), transparent 55%), linear-gradient(180deg, #f5f7fb 0%, #eef2f8 100%)',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '20px', md: '28px' },
          bgcolor: '#fff',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.07)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 4, md: 6 },
            backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.055) 1.15px, transparent 1.15px)',
            backgroundSize: '22px 22px',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              top: 48,
              right: 48,
              width: 168,
              p: 1.5,
              borderRadius: '16px',
              bgcolor: 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
              transform: 'rotate(3deg)',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ height: 8, width: '46%', borderRadius: 99, bgcolor: '#e0e7ff', mb: 1 }} />
            <Box sx={{ height: 8, width: '78%', borderRadius: 99, bgcolor: '#f1f5f9', mb: 0.75 }} />
            <Box sx={{ height: 8, width: '62%', borderRadius: 99, bgcolor: '#f1f5f9' }} />
          </Box>
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              bottom: 56,
              left: 56,
              width: 148,
              p: 1.5,
              borderRadius: '16px',
              bgcolor: 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
              transform: 'rotate(-4deg)',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ height: 64, borderRadius: '10px', bgcolor: '#f8fafc', mb: 1.25 }} />
            <Box sx={{ height: 8, width: '70%', borderRadius: 99, bgcolor: '#e2e8f0' }} />
          </Box>

          <Box sx={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 36, sm: 48 },
                lineHeight: 1.02,
                fontWeight: 700,
                letterSpacing: '-0.04em',
                color: '#0b1220',
              }}
            >
              {copy.createTitleLead}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  lineHeight: 1.25,
                  pb: '0.14em',
                  backgroundImage: 'linear-gradient(100deg, #4f46e5 0%, #2563eb 52%, #0891b2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {copy.createTitleAccent}
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 1.75,
                maxWidth: 420,
                fontSize: 15.5,
                color: '#64748b',
                lineHeight: 1.6,
              }}
            >
              {copy.createSubtitle}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3.5 }}>
              <Box component="label" htmlFor="board-name" sx={{ display: 'block' }}>
                <Typography
                  component="span"
                  sx={{ display: 'block', mb: 0.85, fontSize: 13, fontWeight: 600, color: '#334155' }}
                >
                  {copy.hashtagLabel}
                </Typography>
                <Box
                  component="input"
                  id="board-name"
                  autoFocus
                  value={value}
                  placeholder={copy.hashtagPlaceholder}
                  disabled={creating}
                  autoComplete="off"
                  maxLength={64}
                  aria-invalid={Boolean(error)}
                  onChange={e => setValue(e.target.value.replace(/^#+/, ''))}
                  sx={{
                    ...fieldSx,
                    ...(error ? { borderColor: '#fca5a5' } : null),
                  }}
                />
              </Box>

              <Box component="label" htmlFor="board-goal" sx={{ display: 'block', mt: 2 }}>
                <Typography
                  component="span"
                  sx={{ display: 'block', mb: 0.85, fontSize: 13, fontWeight: 600, color: '#334155' }}
                >
                  {copy.descriptionLabel}
                </Typography>
                <Box
                  component="textarea"
                  id="board-goal"
                  value={description}
                  placeholder={copy.descriptionPlaceholder}
                  disabled={creating}
                  autoComplete="off"
                  maxLength={500}
                  rows={3}
                  aria-invalid={Boolean(error)}
                  onChange={e => setDescription(e.target.value.slice(0, 500))}
                  sx={{
                    ...fieldSx,
                    resize: 'none',
                    minHeight: 96,
                    ...(error ? { borderColor: '#fca5a5' } : null),
                  }}
                />
              </Box>

              {error ? (
                <Typography role="alert" sx={{ mt: 1.25, fontSize: 13, color: '#b91c1c' }}>
                  {error}
                </Typography>
              ) : null}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={creating || !boardName}
                sx={{
                  mt: 2.5,
                  py: 1.35,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 650,
                  fontSize: 15,
                  gap: 0.75,
                  boxShadow: 'none',
                  bgcolor: '#111827',
                  '&:hover': {
                    bgcolor: '#1f2937',
                    boxShadow: 'none',
                    '& .create-arrow': { transform: 'translateX(3px)' },
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#eef0f4',
                    color: '#98a2b3',
                  },
                }}
              >
                {creating ? copy.creating : copy.createCta}
                {creating ? null : (
                  <ArrowForwardRoundedIcon
                    className="create-arrow"
                    sx={{ fontSize: 18, transition: 'transform 0.15s ease' }}
                  />
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const HowToPinGuide = () => {
  const { geminiLanguage } = useGeminiLanguage();
  const copy = getCopy(geminiLanguage);

  return (
    <Box sx={shellSx}>
      <Box sx={{ maxWidth: 1080, mx: 'auto', textAlign: 'center' }}>
        <Chip
          label={copy.emptyChip}
          sx={{
            mb: 2,
            bgcolor: 'rgba(37, 99, 235, 0.10)',
            color: '#1d4ed8',
            fontWeight: 700,
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: 40, sm: 54 },
            lineHeight: 1.1,
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.02em',
          }}
        >
          {copy.pinTitle}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1180, mx: 'auto', mt: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            mb: 5,
            p: { xs: 2, sm: 3 },
            borderRadius: 4,
            bgcolor: 'rgba(255, 255, 255, 0.88)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 18px 50px rgba(37, 99, 235, 0.10)',
          }}
        >
          <Typography sx={{ fontSize: { xs: 24, sm: 32 }, fontWeight: 700, color: '#0f172a' }}>
            {copy.step1Title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 16,
              color: '#475569',
              lineHeight: 1.6,
            }}
          >
            {copy.step1Description}
          </Typography>
          <Box
            component="img"
            src={extra2}
            alt="Pin from the browser extension"
            sx={{
              mt: 3,
              display: 'block',
              mx: 'auto',
              width: '80%',
              maxWidth: '1400px',
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
            }}
          />
        </Box>
        <Box
          sx={{
            mb: 5,
            p: { xs: 2, sm: 3 },
            borderRadius: 4,
            bgcolor: 'rgba(255, 255, 255, 0.88)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 18px 50px rgba(37, 99, 235, 0.10)',
          }}
        >
          <Typography sx={{ fontSize: { xs: 24, sm: 32 }, fontWeight: 700, color: '#0f172a' }}>
            {copy.step2Title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 16,
              color: '#475569',
              lineHeight: 1.6,
            }}
          >
            {copy.step2Description}
          </Typography>
          <Box
            component="img"
            src={canvasRightClickImage}
            alt={copy.step2Title}
            sx={{
              mt: 3,
              display: 'block',
              mx: 'auto',
              width: '80%',
              maxWidth: '1400px',
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const Welcome = ({
  mode = 'how-to-pin',
  onCreateCanvas,
  creating = false,
  error = null,
}) => {
  if (mode === 'create-canvas') {
    return (
      <CreateCanvasGuide
        onCreateCanvas={onCreateCanvas}
        creating={creating}
        error={error}
      />
    );
  }
  return <HowToPinGuide />;
};

export default Welcome;
